import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import AnforderungenSuche from "../../../components/betriebsakte/bescheid/AnforderungenSuche";
import EABetriebsakteDatenContainer from "../../../components/betriebsakte/EABetriebsakteDatenContainer";
import EABetriebsakteMainContainer from "../../../components/betriebsakte/EABetriebsakteMainContainer";
import EATabs from "../../../components/EATabs";
import {ANLAGE, BESCHEID, MESSSTELLE} from "../../../consts/fachobjekt";
import {getAnlagenByBetriebEndpoint} from "../../../services/AnlagenService";
import {getAuflagenartEndpoint, getUsedAnforderungsparameterByBetriebEndpoint} from "../../../services/WertelistenService";
import {useHttp} from "../../../services/HttpService";
import {getAktiveMessstellenByBetriebEndpoint} from "../../../services/Messstellenservice";
import {
    createCalculatedColumn,
    createColumn,
    createComponentColumn,
    createDateColumn,
    createNumberColumn,
} from "../../../utils/ColumnUtils";
import AuflagenAuswertenComponent from "../../../components/betriebsakte/bescheid/AuflagenAuswertenComponent";
import {useHttpSortById} from "../../../utils/usePreformattedHttp";
import {Tooltip} from "@material-ui/core";
import DescriptionIcon from '@material-ui/icons/Description';
import {formatDate} from "../../../utils/DateUtils";
import EAButtonBar from "../../../components/EAButtonBar";
import EADownloadButton from "../../../components/EADownloadButton";
import {postBescheidUebersichtExportEndpointConfiguration} from "../../../services/BescheidsService";
import {getBetriebEndpoint} from "../../../services/BetriebeService";
import QuickLink from "../../../components/betriebsakte/common/QuickLink";
import "../../../styles/font/styles.css";

const tab = {
    festlegungen: "BEHOERDLICHE_FESTLEGUNG",
    selbstueberwachungsauflagen: "SELBSTUEBERWACHUNGSAUFLAGE",
    auflagen: "AUFLAGEN",
};

function ParameterUebersichtView({betriebsId, setToggleAllowed}) {
    const [selectedTab, setSelectedTab] = useState(tab.festlegungen);
    const [loading, setLoading] = useState(false);
    const [selectedParameter, setSelectedParameter] = useState([]);
    const [selectedAuflagen, setSelectedAuflagen] = useState([]);
    const [hasFirstLevelEntlastungsanlagen, setHasFirstLevelEntlastungsanlagen] = useState(false);

    const [anlagenOpts] = useHttp(getAnlagenByBetriebEndpoint(betriebsId));
    const [auflagenArtOpts] = useHttpSortById(getAuflagenartEndpoint);
    const [messstellenOpts] = useHttp(getAktiveMessstellenByBetriebEndpoint(betriebsId));
    const [parameterOpts] = useHttp(getUsedAnforderungsparameterByBetriebEndpoint(betriebsId));
    const [betrieb] = useHttp(getBetriebEndpoint(betriebsId));

    const history = useHistory();

    useEffect(() => setHasFirstLevelEntlastungsanlagen(betrieb?.data?.betriebstyp?.id === "KANAL_KOMMUNAL"), [betrieb]);
    useEffect(() => setToggleAllowed(false), [setToggleAllowed]);

    const getExportTypeForCurrentlySelectedTab = () => {
        switch (selectedTab) {
            case "BEHOERDLICHE_FESTLEGUNG":
                return {endpoint: "festlegungen-erklaerungen", selection: selectedParameter};
            case "SELBSTUEBERWACHUNGSAUFLAGE":
                return {endpoint: "selbstueberwachungsauflagen", selection: selectedParameter};
            case "AUFLAGEN":
                return {endpoint: "auflagen", selection: selectedAuflagen};
            default:
                return {endpoint: "", selection: ""};
        }
    };

    const getExportConfiguration = () => {
        const exportType = getExportTypeForCurrentlySelectedTab();
        return postBescheidUebersichtExportEndpointConfiguration(exportType.selection, exportType.endpoint);
    };

    const allowExport = () => {
        const exportType = getExportTypeForCurrentlySelectedTab();
        const relevantSelection = exportType.selection;
        return relevantSelection && relevantSelection.length > 0;
    }

    const defaultColumnStyling = {
        paddingLeft: "var(--horizontal-spacing)",
        paddingRight: "var(--horizontal-spacing)"
    };

    function getAnforderungenColumns() {
        let columns = [];

        columns.push(
            createComponentColumn(
                "messst-nr",
                (row) => <QuickLink
                    fachobjektbezug={MESSSTELLE}
                    fachobjektId={row.messstelle.id}
                    title={row.messstelle.messstellenId}
                    history={history}
                    hasFirstLevelEntlastungsanlagen={hasFirstLevelEntlastungsanlagen}
                    loading={loading}
                    setLoading={setLoading}
                />,
                "Messst.Nr.",
                null,
                defaultColumnStyling
            )
        );
        columns.push(
            createComponentColumn(
                "bescheid",
                (row) => <QuickLink
                    fachobjektbezug={BESCHEID}
                    fachobjektId={row.bescheid.id}
                    title={buildGueltigAbString(row.bescheid)}
                    history={history}
                    hasFirstLevelEntlastungsanlagen={hasFirstLevelEntlastungsanlagen}
                    loading={loading}
                    setLoading={setLoading}
                />,
                "in Bescheid vom",
                null,
                defaultColumnStyling
            )
        );
        columns.push(createCalculatedColumn("mehrereOptionen",
            (row) => {
                if (row.mehrereOptionen === true) {
                    return <div className={"tooltip"}>
                        <span className="red-bold">!</span>
                        <span className="tooltiptext">Für diesen Parameter existieren verschiedene Werte.</span>
                    </div>;
                }
                return "";
            },
            ""));
        columns.push(
            createCalculatedColumn(
                "parameter",
                (row) => parseNullableBoxedValue(row.parameterNr),
                "Parameter"
            )
        );

        if (selectedTab === tab.selbstueberwachungsauflagen) {
            columns.push(
                createCalculatedColumn(
                    "einheit",
                    (row) => parseNullableBoxedValue(row.einheit),
                    "Einheit"
                )
            );
            columns.push(createColumn("probenahme.value", "Probenahme"));
            columns.push(
                createCalculatedColumn(
                    "haeufigkeit",
                    (row) => parseNullableBoxedValue(row.haeufigkeit),
                    "Häufigkeit"
                )
            );
        } else if (selectedTab === tab.festlegungen) {
            columns.push(createCalculatedColumn(
                "anforderungArt",
                (row) => parseAnforderungArt(row.artAnforderungWert),
                "Anf. Art"));
            columns.push(createColumn("probenahme.value", "Probenahme"));
            columns.push(createNumberColumn("wert", "Wert", {
                paddingLeft: "var(--horizontal-spacing)",
                paddingRight: "var(--horizontal-spacing)",
                textAlign: "right"
            }));
            columns.push(createCalculatedColumn(
                    "einheit",
                    (row) => parseNullableBoxedValue(row.einheit),
                    "Einheit"
                )
            );
        }

        columns.push(createDateColumn("gueltigAb", "Gültig ab"));
        columns.push(createDateColumn("gueltigBis", "bis"));

        return columns;
    }

    function getAuflagenColumns() {
        let columns = [];

        columns.push(createComponentColumn(
                "abwasseranlage",
                (row) => <QuickLink
                    fachobjektbezug={ANLAGE}
                    fachobjektId={row.abwasseranlage.id}
                    title={row.abwasseranlage.value}
                    history={history}
                    hasFirstLevelEntlastungsanlagen={hasFirstLevelEntlastungsanlagen}
                    loading={loading}
                    setLoading={setLoading}
                />,
                "Abwasseranlage",
                null,
                {paddingLeft: "var(--horizontal-spacing)"}
            )
        );
        columns.push(createComponentColumn(
                "beschreibung",
                (row) => <Tooltip
                    title={<h4 style={{color: "white"}}>{row.beschreibung}</h4>}
                    placement="top"
                    arrow={true}
                >
                    <DescriptionIcon style={{color: 'var(--primary-dark)'}}/>
                </Tooltip>,
                "",
                null,
                {paddingLeft: "1em", textAlign: "left"}
            )
        );

        columns.push(createCalculatedColumn(
                "art",
                (row) => parseNullableBoxedValue(row.art),
                "Art"
            )
        );

        columns.push(createDateColumn("fristdatum", "Fristdatum"));

        columns.push(createComponentColumn(
                "bescheid",
                (row) => <QuickLink
                    fachobjektbezug={BESCHEID}
                    fachobjektId={row.bescheid.id}
                    history={history}
                    hasFirstLevelEntlastungsanlagen={hasFirstLevelEntlastungsanlagen}
                    title={buildGueltigAbString(row.bescheid)}
                    loading={loading}
                    setLoading={setLoading}
                />,
                "in Bescheid vom",
                null
            )
        );

        columns.push(createDateColumn("gueltigBis", "gültig bis"));
        return columns;
    }

    const buildGueltigAbString = (bescheid) => {
        if (!bescheid) {
            return "";
        }

        const dateStr = formatDate(bescheid.bescheidsdatum);
        const bescheidQuality = bescheid.bescheidQualitaet.value;
        return bescheidQuality + " - " + dateStr;
    };


    const parseAnforderungArt = (anfArt) => {
        if (!anfArt || !anfArt.value) {
            return "-";
        }

        const unboxedValue = anfArt.value;
        if (unboxedValue.toLowerCase() === "höchstwert") {
            return "HW";
        } else if (unboxedValue.toLowerCase() === "überwachungswert") {
            return "ÜW";
        } else {
            return unboxedValue;
        }
    };

    const parseNullableBoxedValue = (item) => (!item || !item.value) ? "-" : item.value;

    const handleParameterChange = (selectedIndices, allData) => {
        const selectedItems = [];
        selectedIndices.forEach(index => selectedItems.push(allData[index]));
        setSelectedParameter(selectedItems);
    };

    return (
        <EABetriebsakteMainContainer>
            <EATabs
                value={selectedTab}
                onChange={(newSelectedTab) => setSelectedTab(newSelectedTab)}
                options={[tab.festlegungen, tab.selbstueberwachungsauflagen, tab.auflagen]}
                optionLabels={{
                    [tab.festlegungen]: "Festlegungen/Erklärungen",
                    [tab.selbstueberwachungsauflagen]: "Selbstüberwachungsauflagen",
                    [tab.auflagen]: "Auflagen",
                }}
            />
            <EABetriebsakteDatenContainer>
                <AnforderungenSuche
                    betriebsId={betriebsId}
                    anlagenOpts={anlagenOpts}
                    messstellenOpts={messstellenOpts}
                    parameterOpts={parameterOpts}
                    anforderungArt={tab.festlegungen}
                    gridColumns={getAnforderungenColumns()}
                    visible={selectedTab === tab.festlegungen}
                    onSelectionChange={handleParameterChange}
                />
                <AnforderungenSuche
                    betriebsId={betriebsId}
                    anlagenOpts={anlagenOpts}
                    messstellenOpts={messstellenOpts}
                    parameterOpts={parameterOpts}
                    anforderungArt={tab.selbstueberwachungsauflagen}
                    gridColumns={getAnforderungenColumns()}
                    visible={selectedTab === tab.selbstueberwachungsauflagen}
                    onSelectionChange={handleParameterChange}
                />
                <AuflagenAuswertenComponent
                    betriebsId={betriebsId}
                    anlagenOpts={anlagenOpts}
                    auflagenartOpts={auflagenArtOpts}
                    gridColumns={getAuflagenColumns()}
                    visible={selectedTab === tab.auflagen}
                    onSelectionChange={setSelectedAuflagen}
                />
                <EAButtonBar style={{marginTop: "var(--buttonBar-spacing-top)", marginLeft: "var(--buttonBar-spacing-left)"}}>
                    <EADownloadButton
                        id={"exportArbeitsliste"}
                        loading={loading}
                        label={"Excel-Export"}
                        disabled={!allowExport()}
                        config={getExportConfiguration()}
                        style={{marginRight: "auto"}}
                    />
                </EAButtonBar>
            </EABetriebsakteDatenContainer>
        </EABetriebsakteMainContainer>
    );
}

export default ParameterUebersichtView;
